import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import client from '../../../lib/feathers';

/**
 * "Your winery, this week" — the reason to come back.
 *
 * Six wineries have claimed a listing and exactly one has ever edited it. A
 * checklist gets someone through a first visit; what earns a second is evidence
 * it mattered. We already collect that — `industry/listing-basic-stats` returns
 * seven-day action counts against the prior week, the terms people searched to
 * find them, and their average position in results — and per the tier table it
 * is a CLAIMED-tier feature, so every one of those six is entitled to it today.
 *
 * It just lived behind the tenth of thirteen tabs. This puts it on the overview.
 */

// Only the actions a winery would actually care about, in the order they'd care.
// `load` is the headline: someone opened your page.
const INTERESTING = [
  { action: 'load', label: 'Opened your page' },
  { action: 'search', label: 'Found you in search' },
  { action: 'checkin', label: 'Checked in' },
  { action: 'sip', label: 'Logged one of your wines' },
  { action: 'directions', label: 'Asked for directions' },
  { action: 'website', label: 'Clicked through to your site' },
  { action: 'save', label: 'Saved you' },
];

const countOf = (rows, action) => {
  const row = (rows || []).find((r) => r.action === action);
  return row ? parseInt(row.count, 10) : 0;
};

/**
 * Week-over-week, but only when it is good news — and that is not spin.
 *
 * Measured on the real data: 175 platform-wide actions this week, 675 the week
 * before, 21 the week before that. At that volume a week-over-week swing is
 * mostly noise about how busy the whole platform was, so a red "down 12" tells a
 * winery they are failing at something they did not do. The absolute number is
 * always shown, and all-time gives the honest denominator; we simply decline to
 * draw a trend from five data points.
 *
 * Revisit once weekly volume is stable enough for a fall to mean something.
 */
function Delta({ now, before, total }) {
  const up = before > 0 && now > before;
  if (up) {
    return (
      <span className="text-xs text-green-700">↑ {now - before} on last week</span>
    );
  }
  if (total > now) {
    return <span className="text-xs text-gray-400">{total} all time</span>;
  }
  return null;
}

export default function ThisWeekPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!listing?.id) return;
      setLoading(true);
      try {
        const res = await client.service('industry/listing-basic-stats').get(listing.id);
        if (!cancelled) setStats(res);
      } catch (e) {
        console.log('basic stats load failed', e);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listing?.id]);

  if (loading || !stats) return null;

  const rows = INTERESTING
    .map((i) => ({
      ...i,
      now: countOf(stats.current, i.action),
      before: countOf(stats.previous, i.action),
      total: countOf(stats.totals, i.action),
    }))
    .filter((r) => r.now > 0 || r.total > 0);

  // A quiet week is not a reason to show an empty box.
  if (rows.length === 0) return null;

  const terms = [...new Set((stats.directSearches || [])
    .map((s) => (s.search_term || '').trim())
    .filter(Boolean))].slice(0, 6);

  const searchPos = (stats.avgPosition || []).find((p) => p.action === 'search');

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6 flex items-baseline justify-between gap-4">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Your winery, this week</h3>
        <NavLink
          to={`/industry/listings/${listing.id}/reports`}
          className="text-sm text-red-800 hover:underline shrink-0"
        >
          See all reports
        </NavLink>
      </div>

      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
          {rows.map((r) => (
            <div key={r.action}>
              <dd className="text-2xl font-semibold text-gray-900 tabular-nums">{r.now}</dd>
              <dt className="text-sm text-gray-600">{r.label}</dt>
              <div className="mt-0.5"><Delta now={r.now} before={r.before} total={r.total} /></div>
            </div>
          ))}
        </dl>

        {(terms.length > 0 || searchPos) && (
          <div className="mt-6 pt-5 border-t border-gray-200 space-y-3">
            {terms.length > 0 && (
              <div>
                <div className="text-sm text-gray-600">People searched for</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {terms.map((t) => (
                    <span
                      key={t}
                      className="px-2.5 py-1 text-sm rounded-full bg-gray-100 text-gray-800"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {searchPos && (
              <div className="text-sm text-gray-600">
                Average position in search results:{' '}
                <span className="font-medium text-gray-900">
                  {Number(searchPos.avg_position).toFixed(1)}
                </span>
                <span className="text-gray-400"> across {searchPos.appearances} appearances</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
